import React from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Tab,
  Tabs,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Calculate, CalendarMonth, GridView, Lock, PhoneIphone, Spellcheck, Timeline } from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { hasPremiumSubscription } from '../utils/subscription';
import PremiumTrialPopup from '../components/PremiumTrialPopup';
import { usePremiumFeatureAccess } from '../hooks/usePremiumFeatureAccess';
import {
  calculateVedicNumerology,
  reduceToSingleDigit,
  VedicNumerologyResult
} from '../utils/numerologyService';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';
const GRID_ORDER = [3, 1, 9, 6, 7, 5, 2, 8, 4];
const VEDIC_NUMEROLOGY_URL = 'https://kundlistarstudio.com/vedic-numerology';
const BASIC_COLOR = '#7a3f00';
const BASE_COLOR = '#1d4ed8';
const DESTINY_COLOR = '#15803d';
const MD_COLOR = '#dc2626';
const AD_BADGE_COLOR = '#b5c800';
const PRATYANTAR_COLOR = '#d97706';
const ANNUAL_HEADER_COLOR = '#2e7d52';
const ANNUAL_CURRENT_HEADER_COLOR = '#fff8e1';

type NumerologyPeriod = {
  number: number;
  sequence?: number;
  startDate: string;
  endDate: string;
  durationDays: number;
  label?: string;
  parentLabel?: string;
};

type DynamicChartMode = 'mahadasha' | 'antardasha' | 'pratyantar';

interface CorrectionStatus {
  status: string;
  score: number;
  message: string;
}

interface NameCorrectionResult {
  current: {
    name: string;
    dob: string;
    moolank: number;
    bhagyank: number;
    nameTotal: number;
    nameNumber: number;
    luckyNumbers: number[];
    bestNumbers: number[];
    status: CorrectionStatus;
  };
  suggestions: Array<{
    suggestedName: string;
    nameTotal: number;
    nameNumber: number;
    status: CorrectionStatus;
    rankScore: number;
    reason: string;
  }>;
}

interface MobileCorrectionResult {
  mobile: string;
  dob: string;
  digits: string;
  moolank: number;
  bhagyank: number;
  mobileTotal: number;
  mobileNumber: number;
  bestNumbers: number[];
  luckyNumbers: number[];
  mobileSignals?: Array<{
    type: string;
    severity: 'good' | 'warning' | 'danger';
    title: string;
    message: string;
  }>;
  status: CorrectionStatus;
}

interface NumerologySuggestionsResult {
  userNumbers: Record<string, any>;
  preferredNumbers: any[];
  avoidNumbers: any[];
  suggestions: Record<string, any>;
}

interface YearlyNumerologyPlanResult {
  personalYear: {
    number: number;
    planet: string;
    title: string;
    publicSummary: string;
    premiumSummary?: string | null;
    locked: boolean;
  };
  months: Array<{
    month: number;
    monthName: string;
    personalMonth: number;
    planet: string;
    title: string;
    publicSummary: string;
    goodDates: Array<{ date: string; planet: string; personalDay: number }>;
    lockedDatesCount: number;
    locked: boolean;
  }>;
  eventForecast: Array<{
    key: string;
    title: string;
    score: number | null;
    rating: string;
    publicSummary: string;
    locked: boolean;
    lockMessage: string;
  }>;
  cta?: {
    title: string;
    description: string;
  } | null;
  disclaimer: string;
}

type MobileSignal = NonNullable<MobileCorrectionResult['mobileSignals']>[number];

const isDateInPeriod = (period: NumerologyPeriod, target = new Date()) => {
  const start = new Date(`${period.startDate}T00:00:00`);
  const end = new Date(`${period.endDate}T23:59:59`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
  return target >= start && target <= end;
};

const dynamicDashaDurations: Record<number, number> = {
  1: 8,
  2: 16,
  3: 24,
  4: 32,
  5: 40,
  6: 48,
  7: 56,
  8: 64,
  9: 72,
};

const nextDashaNumber = (num: number) => (num === 9 ? 1 : num + 1);

const weekdayMap: Record<number, number> = {
  0: 1,
  1: 2,
  2: 9,
  3: 5,
  4: 3,
  5: 6,
  6: 8,
};

const reduceToSingleDigitNumber = (value: number) => {
  let sum = Math.abs(value)
    .toString()
    .split('')
    .reduce((total, digit) => total + Number(digit), 0);

  while (sum > 9) {
    sum = sum
      .toString()
      .split('')
      .reduce((total, digit) => total + Number(digit), 0);
  }

  return sum;
};

const parseNumerologyDate = (dateString: string) => {
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
    const [day, month, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day);
  }

  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const addDateYears = (date: Date, years: number) => {
  const next = new Date(date);
  next.setFullYear(next.getFullYear() + years);
  return next;
};

const addDateDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const diffDays = (start: Date, end: Date) => {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.max(1, Math.round((end.getTime() - start.getTime()) / msPerDay));
};

const toLocalDateString = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const localizeMobileSignal = (
  signal: MobileSignal,
  bestNumbers: number[],
  isHindi: boolean,
  planetLabel: (num: number) => string
) => {
  if (!isHindi) {
    return {
      title: signal.title.replace(/root/gi, 'number').replace(/reduces to/gi, 'becomes'),
      message: signal.message
        .replace(/recommended roots/gi, 'recommended lucky numbers')
        .replace(/sensitive root/gi, 'sensitive number vibration')
        .replace(/reduces to/gi, 'becomes'),
    };
  }

  const repeatedDigit = Number(signal.title.match(/\d+/)?.[0] || 0);
  const signalNumber = Number(signal.message.match(/\d+/)?.[0] || signal.title.match(/\d+/)?.[0] || 0);
  const luckyText = bestNumbers.join(', ');

  switch (signal.type) {
    case 'zero-pair':
      return {
        title: 'डबल जीरो मौजूद है',
        message: '00 स्पष्टता कम कर सकता है और रुकावट दे सकता है; संभव हो तो बार-बार 0 आने से बचें।',
      };
    case 'repeated-sensitive':
      return {
        title: `${repeatedDigit} बार-बार आया है`,
        message: `कई ${repeatedDigit} होने से ${planetLabel(repeatedDigit)} का प्रभाव बढ़ता है।`,
      };
    case 'multiple-48':
      return {
        title: '4/8 का प्रभाव अधिक है',
        message: 'राहु-शनि का प्रभाव ज्यादा भारी महसूस हो सकता है; संतुलन देने वाले नंबर बेहतर रहेंगे।',
      };
    case 'adjacent-48':
      return {
        title: '4-8 साथ में है',
        message: '4 और 8 साथ आना भारी संयोजन माना जाता है; मोबाइल सुधार में सावधानी रखें।',
      };
    case 'sensitive-adjacency':
      return {
        title: '7 के साथ संवेदनशील संयोजन',
        message: '7 का 4/8 के साथ आना आध्यात्मिक तीव्रता और व्यावहारिक अस्थिरता बढ़ा सकता है।',
      };
    case 'sensitive-total':
      return {
        title: `मोबाइल कुल ${signalNumber} बनता है`,
        message: `${signalNumber} संवेदनशील नंबर वाइब्रेशन है; इस्तेमाल से पहले इसकी अनुकूलता जांचें।`,
      };
    case 'heavy-repeat':
      return {
        title: 'एक नंबर बहुत बार आया है',
        message: 'एक ही ग्रह की ऊर्जा ज्यादा बढ़ सकती है; संतुलन जरूरी है।',
      };
    case 'supportive-digit':
      return {
        title: 'लकी नंबर मौजूद है',
        message: `इसमें सुझाए गए अच्छे नंबरों में से एक मौजूद है: ${luckyText}।`,
      };
    default:
      return { title: signal.title, message: signal.message };
  }
};

const buildCurrentMahadashaPeriod = (result: VedicNumerologyResult): NumerologyPeriod | null => {
  const dob = parseNumerologyDate(result.dob);
  if (Number.isNaN(dob.getTime())) return null;

  const today = new Date();
  const datedMahadashas = result.mahadashas.map((dasha) => {
    const start = addDateYears(dob, dasha.startAge);
    const exclusiveEnd = addDateYears(dob, dasha.endAge);
    return {
      number: dasha.number,
      sequence: dasha.startAge + 1,
      start,
      exclusiveEnd,
      startDate: toLocalDateString(start),
      endDate: toLocalDateString(addDateDays(exclusiveEnd, -1)),
      durationDays: diffDays(start, exclusiveEnd),
      label: `Age ${dasha.startAge}-${dasha.endAge}`,
    };
  });

  const active = datedMahadashas.find((period) => today >= period.start && today < period.exclusiveEnd);
  const fallback = datedMahadashas.find((period) => today < period.exclusiveEnd) || datedMahadashas[datedMahadashas.length - 1];
  if (!active && !fallback) return null;

  const { start, exclusiveEnd, ...period } = active || fallback;
  return period;
};

const formatDmy = (date: Date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const getDobParts = (dobString: string) => {
  const dob = parseNumerologyDate(dobString);
  if (Number.isNaN(dob.getTime())) return null;
  return { dob, day: dob.getDate(), month: dob.getMonth() + 1, year: dob.getFullYear() };
};

const getAnnualAntardashaNumber = (day: number, month: number, year: number) => {
  const weekdayNum = weekdayMap[new Date(year, month - 1, day).getDay()];
  return reduceToSingleDigit(day + month + (year % 100) + weekdayNum);
};

const getMahadashaNumberForAge = (result: VedicNumerologyResult, age: number) => (
  result.mahadashas.find((dasha) => age >= dasha.startAge && age < dasha.endAge)
  || result.mahadashas[result.mahadashas.length - 1]
)?.number || result.ruling;

const buildAnnualDashaCards = (result: VedicNumerologyResult, totalCards = 8) => {
  const parts = getDobParts(result.dob);
  if (!parts) return [];

  const today = new Date();
  let currentPeriodEndYear = today.getFullYear();
  let currentPeriodEnd = new Date(currentPeriodEndYear, parts.dob.getMonth(), parts.dob.getDate());
  if (today > currentPeriodEnd) {
    currentPeriodEndYear += 1;
    currentPeriodEnd = new Date(currentPeriodEndYear, parts.dob.getMonth(), parts.dob.getDate());
  }

  const currentEndingAge = currentPeriodEndYear - parts.year;
  const startAge = Math.max(1, currentEndingAge - 2);

  return Array.from({ length: totalCards }, (_, index) => {
    const endingAge = startAge + index;
    const start = addDateDays(addDateYears(parts.dob, endingAge - 1), 1);
    const end = addDateYears(parts.dob, endingAge);
    const exclusiveEnd = addDateDays(end, 1);
    return {
      endingAge,
      start,
      end,
      startLabel: formatDmy(start),
      endLabel: formatDmy(end),
      mdNumber: getMahadashaNumberForAge(result, endingAge - 1),
      adNumber: getAnnualAntardashaNumber(parts.day, parts.month, start.getFullYear()),
      current: today >= start && today < exclusiveEnd,
    };
  });
};

const buildAntardashaPeriods = (mahadasha: NumerologyPeriod | null, dobString: string): NumerologyPeriod[] => {
  if (!mahadasha) return [];

  const dob = parseNumerologyDate(dobString);
  if (Number.isNaN(dob.getTime())) return [];

  const mdStart = parseNumerologyDate(mahadasha.startDate);
  const mdExclusiveEnd = addDateDays(parseNumerologyDate(mahadasha.endDate), 1);
  const periods: NumerologyPeriod[] = [];
  let currentStart = new Date(mdStart);
  let sequence = 1;

  while (currentStart < mdExclusiveEnd) {
    const nextYear = addDateYears(currentStart, 1);
    const currentExclusiveEnd = nextYear < mdExclusiveEnd ? nextYear : mdExclusiveEnd;
    const birthdayThisYear = new Date(currentStart.getFullYear(), dob.getMonth(), dob.getDate());
    const currentNumber = reduceToSingleDigitNumber(
      dob.getDate() + dob.getMonth() + 1 + (currentStart.getFullYear() % 100) + weekdayMap[birthdayThisYear.getDay()]
    );

    periods.push({
      number: currentNumber,
      sequence,
      startDate: toLocalDateString(currentStart),
      endDate: toLocalDateString(addDateDays(currentExclusiveEnd, -1)),
      durationDays: diffDays(currentStart, currentExclusiveEnd),
      parentLabel: mahadasha.label,
      label: `Antardasha ${sequence}`,
    });

    currentStart = currentExclusiveEnd;
    sequence += 1;
  }

  return periods;
};

const buildPratyantarPeriods = (antardasha: NumerologyPeriod | null): NumerologyPeriod[] => {
  if (!antardasha) return [];

  const adStart = parseNumerologyDate(antardasha.startDate);
  const adExclusiveEnd = addDateDays(parseNumerologyDate(antardasha.endDate), 1);
  const periods: NumerologyPeriod[] = [];
  let currentStart = new Date(adStart);
  let currentNumber = antardasha.number;
  let sequence = 1;

  while (sequence <= 9 && currentStart < adExclusiveEnd) {
    const intendedExclusiveEnd = addDateDays(
      currentStart,
      dynamicDashaDurations[currentNumber] + (sequence === 9 ? 5 : 0)
    );
    const currentExclusiveEnd = intendedExclusiveEnd < adExclusiveEnd ? intendedExclusiveEnd : adExclusiveEnd;

    periods.push({
      number: currentNumber,
      sequence,
      startDate: toLocalDateString(currentStart),
      endDate: toLocalDateString(addDateDays(currentExclusiveEnd, -1)),
      durationDays: diffDays(currentStart, currentExclusiveEnd),
      parentLabel: antardasha.label,
      label: `Pratyantara ${sequence}`,
    });

    currentStart = currentExclusiveEnd;
    currentNumber = nextDashaNumber(currentNumber);
    sequence += 1;
  }

  return periods;
};

type GridOverlay = {
  number: number;
  color: string;
  label?: string;
};

const getCellSegments = (
  num: number,
  baseCount: number,
  overlays: GridOverlay[] = []
) => {
  const segments: Array<{ text: string; color: string; label?: string }> = [];
  let plainCount = baseCount;

  overlays.forEach((overlay) => {
    if (overlay.number !== num) return;
    segments.push({ text: String(num), color: overlay.color, label: overlay.label });
  });

  if (plainCount > 0) {
    segments.unshift({ text: String(num).repeat(plainCount), color: BASIC_COLOR });
  }

  return segments;
};

const AnnualDashaGridCard: React.FC<{
  result: VedicNumerologyResult;
  card: ReturnType<typeof buildAnnualDashaCards>[number];
}> = ({ result, card }) => (
  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: card.current ? '#d97706' : 'rgba(46, 125, 82, 0.22)', bgcolor: '#fffdf7', boxShadow: card.current ? '0 12px 28px rgba(217, 119, 6, 0.14)' : '0 8px 22px rgba(23, 53, 39, 0.06)' }}>
    <Box
      sx={{
        bgcolor: card.current ? ANNUAL_CURRENT_HEADER_COLOR : '#f8fff9',
        color: card.current ? '#7a3f00' : ANNUAL_HEADER_COLOR,
        textAlign: 'center',
        py: 1,
        px: 1.25,
        mb: 1.25,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        border: '1px solid rgba(46, 125, 82, 0.16)',
        borderRadius: 1.5,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
        {card.startLabel}-{card.endLabel}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
        ({card.endingAge} Yrs.)
      </Typography>
    </Box>
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', border: '2px solid #92400e', boxShadow: '0 10px 24px rgba(146, 64, 14, 0.08)' }}>
      {GRID_ORDER.map((num) => {
        const baseSegments = getCellSegments(num, result.grid?.[num] || 0, [
          { number: result.ruling, color: BASE_COLOR, label: 'Base' },
          { number: result.destiny, color: DESTINY_COLOR, label: 'Destiny' },
        ]);
        const badges = [
          card.mdNumber === num ? { label: String(num), color: MD_COLOR } : null,
          card.adNumber === num ? { label: String(num), color: AD_BADGE_COLOR } : null,
        ].filter(Boolean) as Array<{ label: string; color: string }>;

        return (
          <Box
            key={num}
            sx={{
              minHeight: 72,
              aspectRatio: '1 / 1',
              borderRight: '1px solid #b7791f',
              borderBottom: '1px solid #b7791f',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
              px: 0.5,
              bgcolor: baseSegments.length || badges.length ? '#fff8e1' : '#fffdf7',
            }}
          >
            {!!baseSegments.length && (
              <Typography component="span" sx={{ fontSize: 22, lineHeight: 1, fontWeight: 500 }}>
                {baseSegments.map((segment, index) => (
                  <Typography key={`${num}-${segment.color}-${index}`} component="span" sx={{ color: segment.color, fontWeight: 600 }}>
                    {segment.text}
                  </Typography>
                ))}
              </Typography>
            )}
            {badges.map((badge, index) => (
              <Box
                key={`${num}-${badge.color}-${index}`}
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  bgcolor: badge.color,
                  color: '#fff',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  fontSize: 18,
                  boxShadow: '0 4px 10px rgba(0,0,0,0.12)',
                }}
              >
                {badge.label}
              </Box>
            ))}
          </Box>
        );
      })}
    </Box>
  </Paper>
);

const GridLegend: React.FC<{ items: Array<{ label: string; color: string; value?: number | null }> }> = ({ items }) => (
  <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
    {items.map((item) => (
      <Stack key={`${item.label}-${item.color}`} direction="row" spacing={0.75} alignItems="center">
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color }} />
        <Typography variant="caption" color="text.secondary">
          {item.label}{item.value ? ` ${item.value}` : ''}
        </Typography>
      </Stack>
    ))}
  </Stack>
);

const DynamicNumerologyGrid: React.FC<{
  grid: Record<number, number>;
  overlays: GridOverlay[];
  compact?: boolean;
}> = ({ grid, overlays, compact = false }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      border: '1px solid rgba(46, 125, 82, 0.28)',
      bgcolor: '#fbfffc',
      minWidth: 0,
    }}
  >
    {GRID_ORDER.map((num) => {
      const count = grid?.[num] || 0;
      const segments = getCellSegments(num, count, overlays);
      return (
        <Box
          key={num}
          sx={{
            minHeight: compact ? 54 : 72,
            borderRight: '1px solid rgba(46, 125, 82, 0.16)',
            borderBottom: '1px solid rgba(46, 125, 82, 0.16)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 0.75,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: compact ? 16 : 20,
              lineHeight: 1.1,
              fontWeight: 600,
              color: '#173527',
              wordBreak: 'break-word',
              textAlign: 'center',
            }}
          >
            {segments.map((segment, index) => (
              <Typography
                key={`${num}-${segment.color}-${index}`}
                component="span"
                title={segment.label}
                sx={{ color: segment.color, fontWeight: 600 }}
              >
                {segment.text}
              </Typography>
            ))}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

const DynamicDashaCharts: React.FC<{
  result: VedicNumerologyResult;
  activeLocale: string;
  planetLabel: (num: number) => string;
  formatDisplayDate: (date: string, locale: string) => string;
}> = ({ result, activeLocale, planetLabel, formatDisplayDate }) => {
  const [mode, setMode] = React.useState<DynamicChartMode>('mahadasha');
  const currentMahadasha = React.useMemo(() => buildCurrentMahadashaPeriod(result), [result]);
  const antardashaPeriods = React.useMemo(() => buildAntardashaPeriods(currentMahadasha, result.dob), [currentMahadasha, result.dob]);
  const activeAntardasha = antardashaPeriods.find((period) => isDateInPeriod(period)) || antardashaPeriods[0] || null;
  const [selectedAntardashaKey, setSelectedAntardashaKey] = React.useState<string | null>(null);
  React.useEffect(() => {
    setSelectedAntardashaKey(null);
  }, [result.dob, currentMahadasha?.startDate, currentMahadasha?.endDate]);
  const selectedAntardasha = antardashaPeriods.find((period) => `${period.startDate}-${period.number}` === selectedAntardashaKey)
    || activeAntardasha;
  const pratyantarPeriods = React.useMemo(() => buildPratyantarPeriods(selectedAntardasha), [selectedAntardasha]);
  const periods: NumerologyPeriod[] = mode === 'mahadasha'
    ? currentMahadasha ? [currentMahadasha] : []
    : mode === 'antardasha'
      ? antardashaPeriods
      : pratyantarPeriods;
  const modeDescription = mode === 'mahadasha'
    ? 'Current Mahadasha grid with the active major number highlighted.'
    : mode === 'antardasha'
      ? 'Antardasha grids inside the current Mahadasha. Select a card to view its Pratyantara.'
      : `Pratyantara grids inside ${selectedAntardasha ? `${formatDisplayDate(selectedAntardasha.startDate, activeLocale)} - ${formatDisplayDate(selectedAntardasha.endDate, activeLocale)}` : 'the selected Antardasha'}.`;
  const annualCards = React.useMemo(() => buildAnnualDashaCards(result), [result]);

  return (
    <Stack spacing={3}>
    <Card sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(46, 125, 82, 0.16)' }}>
      <CardContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Dynamic Numerology Dasha Charts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {modeDescription}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant={mode === 'mahadasha' ? 'contained' : 'outlined'} color="success" onClick={() => setMode('mahadasha')} sx={{ fontWeight: 600, borderRadius: 2 }}>Mahadasha</Button>
            <Button variant={mode === 'antardasha' ? 'contained' : 'outlined'} color="success" onClick={() => setMode('antardasha')} sx={{ fontWeight: 600, borderRadius: 2 }}>Antardasha</Button>
            <Button variant={mode === 'pratyantar' ? 'contained' : 'outlined'} color="warning" onClick={() => setMode('pratyantar')} sx={{ fontWeight: 600, borderRadius: 2 }}>Pratyantara</Button>
          </Stack>
        </Stack>

        <Grid container spacing={2}>
          {periods.map((period) => {
            const current = isDateInPeriod(period);
            const selected = mode === 'antardasha' && selectedAntardasha
              ? selectedAntardasha.startDate === period.startDate && selectedAntardasha.number === period.number
              : false;
            const dashaOverlays: GridOverlay[] = [
              { number: result.ruling, color: BASE_COLOR, label: 'Base' },
              { number: result.destiny, color: DESTINY_COLOR, label: 'Destiny' },
              ...(currentMahadasha ? [{ number: currentMahadasha.number, color: MD_COLOR, label: 'MD' }] : []),
              ...(mode === 'pratyantar' && selectedAntardasha ? [{ number: selectedAntardasha.number, color: AD_BADGE_COLOR, label: 'AD' }] : []),
              ...(mode === 'antardasha' ? [{ number: period.number, color: AD_BADGE_COLOR, label: 'AD' }] : []),
              ...(mode === 'pratyantar' ? [{ number: period.number, color: PRATYANTAR_COLOR, label: 'PD' }] : []),
            ];
            return (
              <Grid key={`${mode}-${period.sequence || period.number}-${period.startDate}`} size={{ xs: 12, sm: 6, lg: 3 }}>
                <Paper
                  variant="outlined"
                  onClick={() => {
                    if (mode === 'antardasha') {
                      setSelectedAntardashaKey(`${period.startDate}-${period.number}`);
                    }
                  }}
                  sx={{
                    overflow: 'hidden',
                    borderColor: current || selected ? '#d97706' : 'rgba(46, 125, 82, 0.22)',
                    borderRadius: 2,
                    cursor: mode === 'antardasha' ? 'pointer' : 'default',
                    bgcolor: '#ffffff',
                    boxShadow: current || selected ? '0 12px 28px rgba(217, 119, 6, 0.18)' : '0 8px 22px rgba(23, 53, 39, 0.06)',
                    transition: 'transform 180ms ease, box-shadow 180ms ease',
                    '&:hover': mode === 'antardasha' ? {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 14px 30px rgba(23, 53, 39, 0.12)',
                    } : undefined,
                  }}
                >
                  <Box sx={{ bgcolor: current || selected ? '#fef3c7' : '#edf7f0', color: '#173527', px: 1.25, py: 1, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatDisplayDate(period.startDate, activeLocale)} - {formatDisplayDate(period.endDate, activeLocale)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: current || selected ? '#9a3412' : '#2e7d52', fontWeight: 600 }}>
                      {planetLabel(period.number)} · {period.durationDays} days
                    </Typography>
                  </Box>
                  <DynamicNumerologyGrid grid={result.grid} overlays={dashaOverlays} compact />
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
    </Card>

    <Card sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(46, 125, 82, 0.16)' }}>
      <CardContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Annual Numerology Dasha Grid
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Premium yearly grids showing Basic number, Destiny number, Mahadasha and Antardasha together.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={`MD ${currentMahadasha?.number || '-'}`} sx={{ bgcolor: MD_COLOR, color: '#fff', fontWeight: 600 }} />
            <Chip size="small" label={`AD ${activeAntardasha?.number || '-'}`} sx={{ bgcolor: AD_BADGE_COLOR, color: '#fff', fontWeight: 600 }} />
          </Stack>
        </Stack>
        <GridLegend items={[
          { label: 'DOB', color: BASIC_COLOR },
          { label: 'Base', color: BASE_COLOR, value: result.ruling },
          { label: 'Destiny', color: DESTINY_COLOR, value: result.destiny },
          { label: 'MD', color: MD_COLOR },
          { label: 'AD', color: AD_BADGE_COLOR },
        ]} />

        <Grid container spacing={3} sx={{ mt: 1 }}>
          {annualCards.map((card) => (
            <Grid key={`${card.startLabel}-${card.endLabel}`} size={{ xs: 12, sm: 6 }}>
              <AnnualDashaGridCard result={result} card={card} />
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
    </Stack>
  );
};

const PremiumMaskedSection: React.FC<{
  title: string;
  description: string;
  premiumLabel: string;
  ctaLabel: string;
  teaser: 'dasha' | 'yogas';
  onUpgrade: () => void;
}> = ({ title, description, premiumLabel, ctaLabel, teaser, onUpgrade }) => (
  <Card sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(217, 119, 6, 0.24)' }}>
    <CardContent sx={{ position: 'relative', minHeight: teaser === 'dasha' ? 300 : 260 }}>
      <Box sx={{ filter: 'blur(5px)', opacity: 0.6, pointerEvents: 'none', userSelect: 'none' }}>
        {teaser === 'dasha' ? (
          <>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Premium dasha grids, periods and active numbers are waiting here.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                {['Mahadasha', 'Antardasha', 'Pratyantara'].map((label) => (
                  <Button key={label} variant={label === 'Mahadasha' ? 'contained' : 'outlined'} color={label === 'Pratyantara' ? 'warning' : 'success'} sx={{ fontWeight: 600, borderRadius: 2 }}>
                    {label}
                  </Button>
                ))}
              </Stack>
            </Stack>
            <Grid container spacing={2}>
              {[0, 1, 2].map((item) => (
                <Grid key={item} size={{ xs: 12, sm: 6, lg: 3 }}>
                  <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 2 }}>
                    <Box sx={{ bgcolor: item === 0 ? '#fef3c7' : '#edf7f0', px: 1.25, py: 1, textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>00 Mon 0000 - 00 Mon 0000</Typography>
                      <Typography variant="caption" sx={{ color: '#2e7d52', fontWeight: 600 }}>Planet · days</Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
                      {Array.from({ length: 9 }).map((_, index) => (
                        <Box key={index} sx={{ height: 58, border: '1px solid rgba(46,125,82,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600 }}>
                          {index % 2 === 0 ? '●' : ''}
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </>
        ) : (
          <Stack spacing={1.5}>
            <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600, color: '#6d28d9' }}>
              {title}
            </Typography>
            {[0, 1, 2].map((item) => (
              <Paper key={item} variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#faf5ff' }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                  <Chip size="small" color="secondary" label="Plan access" />
                  <Chip size="small" variant="outlined" label="•-•-•" />
                </Stack>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Vedic Grid Yoga
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Unlock to reveal the yoga name, number combination and guidance.
                </Typography>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          background: 'linear-gradient(180deg, rgba(255,255,255,0.68), rgba(255,255,255,0.94))',
        }}
      >
        <Paper elevation={6} sx={{ maxWidth: 460, p: 3, borderRadius: 3, textAlign: 'center', border: '1px solid rgba(249,115,0,0.28)' }}>
          <Lock sx={{ fontSize: 34, mb: 1, color: '#f97300' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
            {premiumLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {description}
          </Typography>
          <Button
            variant="contained"
            onClick={onUpgrade}
            sx={{
              bgcolor: '#f97300',
              color: '#fff',
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              py: 1.1,
              boxShadow: '0 6px 12px rgba(0,0,0,0.22)',
              '&:hover': { bgcolor: '#ea580c' },
            }}
          >
            {ctaLabel}
          </Button>
        </Paper>
      </Box>
    </CardContent>
  </Card>
);

const PLANET_LABELS: Record<number, string> = {
  1: 'Sun',
  2: 'Moon',
  3: 'Jupiter',
  4: 'Rahu',
  5: 'Mercury',
  6: 'Venus',
  7: 'Ketu',
  8: 'Saturn',
  9: 'Mars'
};

const formatDisplayDate = (dateString: string, locale = 'en-IN') => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const normalizeDisplayDateForApi = (value: string) => {
  const display = value.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!display) throw new Error('DOB must be in DD/MM/YYYY format.');

  const [, day, month, year] = display;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (
    date.getFullYear() !== Number(year)
    || date.getMonth() !== Number(month) - 1
    || date.getDate() !== Number(day)
  ) {
    throw new Error('DOB is not a valid calendar date.');
  }

  return `${year}-${month}-${day}`;
};

const formatDateInput = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const VedicNumerology: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { state: authState } = useAuth();
  const [dob, setDob] = React.useState('');
  const [name, setName] = React.useState('');
  const [mobile, setMobile] = React.useState('');
  const [suggestionName, setSuggestionName] = React.useState('');
  const [suggestionDob, setSuggestionDob] = React.useState('');
  const [result, setResult] = React.useState<VedicNumerologyResult | null>(null);
  const [nameCorrection, setNameCorrection] = React.useState<NameCorrectionResult | null>(null);
  const [mobileCorrection, setMobileCorrection] = React.useState<MobileCorrectionResult | null>(null);
  const [suggestionsResult, setSuggestionsResult] = React.useState<NumerologySuggestionsResult | null>(null);
  const [yearlyPlan, setYearlyPlan] = React.useState<YearlyNumerologyPlanResult | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [correctionLoading, setCorrectionLoading] = React.useState<'name' | 'mobile' | null>(null);
  const [suggestionsLoading, setSuggestionsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [correctionError, setCorrectionError] = React.useState<string | null>(null);
  const [suggestionsError, setSuggestionsError] = React.useState<string | null>(null);
  const [source, setSource] = React.useState<'api' | 'local' | null>(null);
  const [activeSubTab, setActiveSubTab] = React.useState<'analysis' | 'correction' | 'suggestions'>('analysis');
  const isPremiumUser = hasPremiumSubscription(authState.user);
  const [showPremiumLock, setShowPremiumLock] = React.useState(false);
  const { popup, loading: trialLoading, checkFeatureAccess, closePopup } = usePremiumFeatureAccess({
    onShowPremiumLock: () => setShowPremiumLock(true)
  });

  React.useEffect(() => {
    document.title = t('vedicNumerologyPage.metaTitle');
  }, [t]);

  const planetLabel = (num: number) => t(`vedicNumerologyPage.planets.${num}.name`, { defaultValue: PLANET_LABELS[num] });
  const planetQuality = (num: number, fallback: string) => t(`vedicNumerologyPage.planets.${num}.quality`, { defaultValue: fallback });
  const planetRemedy = (num: number, fallback: string) => t(`vedicNumerologyPage.planets.${num}.remedy`, { defaultValue: fallback });
  const planeName = (name: string) => t(`vedicNumerologyPage.planes.${name.includes('3-1-9') ? 'mental' : name.includes('6-7-5') ? 'emotional' : 'practical'}.name`, { defaultValue: name });
  const planeDesc = (name: string, fallback: string) => t(`vedicNumerologyPage.planes.${name.includes('3-1-9') ? 'mental' : name.includes('6-7-5') ? 'emotional' : 'practical'}.desc`, { defaultValue: fallback });
  const isHindi = i18n.resolvedLanguage?.startsWith('hi') || i18n.language?.startsWith('hi');
  const activeLocale = isHindi ? 'hi-IN' : 'en-IN';
  const statusKey = (status: string) => status.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  const statusLabel = (status: string) => t(`vedicNumerologyPage.statusValues.${statusKey(status)}`, { defaultValue: status });
  const statusMessage = (status: CorrectionStatus) => t(`vedicNumerologyPage.statusMessages.${statusKey(status.status)}`, { defaultValue: status.message });
  const premiumMaskCopy = {
    premiumLabel: isHindi ? 'ट्रायल समाप्त' : 'Trial ended',
    ctaLabel: isHindi ? 'प्लान चुनें' : 'Choose plan',
    dashaTitle: isHindi ? 'डायनामिक न्यूमेरोलॉजी दशा चार्ट्स' : 'Dynamic Numerology Dasha Charts',
    dashaDescription: isHindi
      ? '3-दिन के ट्रायल के बाद डायनामिक महादशा, अंतर्दशा और प्रत्यंतर चार्ट जारी रखने के लिए Kundlistar Studio प्लान चुनें।'
      : 'Your 3-day trial includes these charts. After trial, choose a Kundlistar Studio plan to keep using dynamic Mahadasha, Antardasha and Pratyantara charts.',
    yogasTitle: isHindi ? 'वैदिक ग्रिड योग' : 'Vedic Grid Yogas',
    yogasDescription: isHindi
      ? 'ट्रायल के बाद वैदिक ग्रिड योग, नंबर कॉम्बिनेशन और ज्योतिषीय संकेत जारी रखने के लिए प्लान चुनें।'
      : 'After trial, choose a plan to continue viewing detected Vedic Grid Yogas, number combinations and astrological indications.',
  };
  const currentMahadashaForTable = React.useMemo(
    () => result ? buildCurrentMahadashaPeriod(result) : null,
    [result]
  );
  const antardashaPeriodsForTable = React.useMemo(
    () => result ? buildAntardashaPeriods(currentMahadashaForTable, result.dob) : [],
    [currentMahadashaForTable, result]
  );
  const activeAntardashaForTable = React.useMemo(
    () => antardashaPeriodsForTable.find((period) => isDateInPeriod(period)) || antardashaPeriodsForTable[0] || null,
    [antardashaPeriodsForTable]
  );
  const pratyantarPeriodsForTable = React.useMemo(
    () => buildPratyantarPeriods(activeAntardashaForTable),
    [activeAntardashaForTable]
  );

  const handleCalculate = async () => {
    if (!dob) {
      setError(t('vedicNumerologyPage.errors.selectDob'));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const apiDob = normalizeDisplayDateForApi(dob);
      const localResult = calculateVedicNumerology(apiDob);
      setResult(localResult);
      setSource('local');

      const response = await axios.post(`${API_BASE_URL}/numerology/calculate`, { dob: apiDob });
      setResult(response.data.data.numerology);
      setSource('api');
    } catch (err: any) {
      if (!result) {
        try {
          setResult(calculateVedicNumerology(normalizeDisplayDateForApi(dob)));
          setSource('local');
          setError(null);
        } catch (localErr: any) {
          setResult(null);
          setSource(null);
          setError(localErr.message || t('vedicNumerologyPage.errors.calculateFailed'));
        }
      } else {
        setError(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNameCorrection = async () => {
    if (!dob || !name.trim()) {
      setCorrectionError(t('vedicNumerologyPage.errors.nameRequired'));
      return;
    }
    if (!authState.token) {
      navigate('/login');
      return;
    }

    try {
      setCorrectionLoading('name');
      setCorrectionError(null);
      const apiDob = normalizeDisplayDateForApi(dob);
      await checkFeatureAccess({
        featureKey: 'VEDIC_NUMEROLOGY',
        apiCall: (trialAccessToken?: string) => axios.post(`${API_BASE_URL}/numerology/name-correction`, {
          dob: apiDob,
          name: name.trim()
        }, {
          headers: {
            Authorization: `Bearer ${authState.token}`,
            ...(trialAccessToken ? { 'x-premium-trial-token': trialAccessToken } : {})
          }
        }),
        onSuccess: (data: any) => {
          setNameCorrection(data.data);
          setShowPremiumLock(false);
        }
      });
    } catch (err: any) {
      setCorrectionError(err.response?.data?.error || err.message || t('vedicNumerologyPage.errors.nameFailed'));
    } finally {
      setCorrectionLoading(null);
    }
  };

  const handleMobileCorrection = async () => {
    if (!dob || !mobile.trim()) {
      setCorrectionError(t('vedicNumerologyPage.errors.mobileRequired'));
      return;
    }
    if (!authState.token) {
      navigate('/login');
      return;
    }

    try {
      setCorrectionLoading('mobile');
      setCorrectionError(null);
      const apiDob = normalizeDisplayDateForApi(dob);
      await checkFeatureAccess({
        featureKey: 'VEDIC_NUMEROLOGY',
        apiCall: (trialAccessToken?: string) => axios.post(`${API_BASE_URL}/numerology/mobile-correction`, {
          dob: apiDob,
          mobile: mobile.trim()
        }, {
          headers: {
            Authorization: `Bearer ${authState.token}`,
            ...(trialAccessToken ? { 'x-premium-trial-token': trialAccessToken } : {})
          }
        }),
        onSuccess: (data: any) => {
          setMobileCorrection(data.data);
          setShowPremiumLock(false);
        }
      });
    } catch (err: any) {
      setCorrectionError(err.response?.data?.error || err.message || t('vedicNumerologyPage.errors.mobileFailed'));
    } finally {
      setCorrectionLoading(null);
    }
  };

  const handleSuggestions = async () => {
    if (!suggestionName.trim() || !suggestionDob) {
      setSuggestionsError('Full name and date of birth are required.');
      return;
    }
    if (!authState.token) {
      navigate('/login');
      return;
    }

    try {
      setSuggestionsLoading(true);
      setSuggestionsError(null);
      const apiDob = normalizeDisplayDateForApi(suggestionDob);
      await checkFeatureAccess({
        featureKey: 'VEDIC_NUMEROLOGY',
        apiCall: (trialAccessToken?: string) => axios.post(`${API_BASE_URL}/numerology/suggestions`, {
          fullName: suggestionName.trim(),
          dateOfBirth: apiDob,
        }, {
          headers: {
            Authorization: `Bearer ${authState.token}`,
            ...(trialAccessToken ? { 'x-premium-trial-token': trialAccessToken } : {})
          },
        }),
        onSuccess: (data: any) => {
          setSuggestionsResult(data.data);
          setShowPremiumLock(false);
          axios.post(`${API_BASE_URL}/numerology/yearly-plan`, {
            fullName: suggestionName.trim(),
            dob: apiDob,
            year: new Date().getFullYear(),
            language: isHindi ? 'hi' : 'en',
          }, {
            headers: {
              Authorization: `Bearer ${authState.token}`,
            },
          })
            .then((yearlyResponse) => setYearlyPlan(yearlyResponse.data.data))
            .catch(() => setYearlyPlan(null));
        }
      });
    } catch (err: any) {
      setSuggestionsError(err.response?.data?.error || err.message || 'Failed to generate numerology suggestions.');
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    if (status === 'Excellent') return 'success';
    if (status === 'Good') return 'primary';
    if (status === 'Average') return 'warning';
    return 'error';
  };

  const softwareStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Kundlistar Vedic Numerology Software',
    applicationCategory: 'LifestyleApplication',
    operatingSystem: 'Web, Android',
    url: VEDIC_NUMEROLOGY_URL,
    description: t('vedicNumerologyPage.metaDescription'),
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'INR'
    },
    featureList: [
      'Moolank and Bhagyank calculator',
      'Surya Yantra numerology grid',
      'Name correction calculator',
      'Mobile number numerology checker',
      'Vedic numerology dasha timelines'
    ],
    brand: {
      '@type': 'Brand',
      name: 'Kundlistar'
    }
  };

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is Kundlistar Vedic numerology software?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Kundlistar Vedic numerology software helps users explore Moolank, Bhagyank, Surya Yantra grid, active planes, missing numbers, name correction, mobile number vibration and numerology dasha timelines.'
        }
      },
      {
        '@type': 'Question',
        name: 'Can I check name correction and mobile number numerology?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Kundlistar includes name correction and mobile number numerology tools based on birth-date numbers and Chaldean name values for astrological guidance.'
        }
      },
      {
        '@type': 'Question',
        name: 'Are numerology results guaranteed predictions?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'No. Kundlistar presents numerology as guidance, spiritual perspective and astrological indication, not as guaranteed outcomes or professional advice.'
        }
      }
    ]
  };

  const renderGridCell = (num: number, overlays: GridOverlay[] = []) => {
    const count = result?.grid?.[num] || 0;
    const segments = getCellSegments(num, count, overlays);
    const isActive = segments.length > 0;

    return (
      <Box
        key={num}
        sx={{
          aspectRatio: '1 / 1',
          border: '1px solid',
          borderColor: '#b7791f',
          bgcolor: isActive ? '#fff8e1' : '#fffdf7',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          minWidth: 0
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {num}
        </Typography>
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, minHeight: 32, wordBreak: 'break-word' }}
        >
          {segments.map((segment, index) => (
            <Typography
              key={`${num}-${segment.color}-${index}`}
              component="span"
              title={segment.label}
              sx={{ color: segment.color, fontWeight: 600 }}
            >
              {segment.text}
            </Typography>
          ))}
        </Typography>
      </Box>
    );
  };

  const renderGridLegend = (items: Array<{ label: string; color: string; value?: number | null }>) => (
    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
      {items.map((item) => (
        <Stack key={`${item.label}-${item.color}`} direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color }} />
          <Typography variant="caption" color="text.secondary">
            {item.label}{item.value ? ` ${item.value}` : ''}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );

  return (
    <>
      <Helmet>
        <title>{t('vedicNumerologyPage.metaTitle')}</title>
        <meta
          name="description"
          content={t('vedicNumerologyPage.metaDescription')}
        />
        <meta
          name="keywords"
          content={t('vedicNumerologyPage.metaKeywords')}
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={VEDIC_NUMEROLOGY_URL} />
        <meta property="og:title" content={t('vedicNumerologyPage.ogTitle')} />
        <meta
          property="og:description"
          content={t('vedicNumerologyPage.ogDescription')}
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={VEDIC_NUMEROLOGY_URL} />
        <meta property="og:image" content="https://kundlistarstudio.com/logo192.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('vedicNumerologyPage.ogTitle')} />
        <meta
          name="twitter:description"
          content={t('vedicNumerologyPage.twitterDescription')}
        />
        <meta name="twitter:image" content="https://kundlistarstudio.com/logo192.png" />
        <script type="application/ld+json">
          {JSON.stringify(softwareStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      </Helmet>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" component="h1" sx={{ mb: 1, fontWeight: 600 }}>
            {t('vedicNumerologyPage.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('vedicNumerologyPage.subtitle')}
          </Typography>
        </Box>

        <Paper
          component="section"
          variant="outlined"
          sx={{
            p: { xs: 2.5, md: 3 },
            mb: 3,
            borderRadius: 3,
            bgcolor: '#fffdf7',
            borderColor: '#f3d18b'
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography component="h2" variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                {t('vedicNumerologyPage.seoIntroTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('vedicNumerologyPage.seoIntroBody')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                {t('vedicNumerologyPage.seoDisclaimer')}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2.25,
                  color: 'text.secondary',
                  '& li': { mb: 0.75 }
                }}
              >
                <li>{t('vedicNumerologyPage.seoFeatureMoolank')}</li>
                <li>{t('vedicNumerologyPage.seoFeatureGrid')}</li>
                <li>{t('vedicNumerologyPage.seoFeatureCorrection')}</li>
                <li>{t('vedicNumerologyPage.seoFeatureDasha')}</li>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={activeSubTab}
            onChange={(_event, value) => setActiveSubTab(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab value="analysis" label={t('vedicNumerologyPage.title')} />
            <Tab value="correction" label={t('vedicNumerologyPage.correction.title')} />
            <Tab value="suggestions" label="Suggestions" />
          </Tabs>
        </Paper>

        {activeSubTab === 'analysis' && (
        <Card sx={{ borderRadius: 3, mb: 4 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="stretch">
              <Grid size={{ xs: 12, md: 9 }}>
                <TextField
                  fullWidth
                  label={t('vedicNumerologyPage.dateOfBirth')}
                  value={dob}
                  onChange={(event) => {
                    setDob(formatDateInput(event.target.value));
                    setError(null);
                  }}
                  placeholder="DD/MM/YYYY"
                  helperText="Use DD/MM/YYYY format."
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Calculate />}
                  onClick={handleCalculate}
                  disabled={loading}
                  sx={{ minHeight: 56, height: '100%', fontWeight: 600 }}
                >
                  {loading ? t('vedicNumerologyPage.calculating') : t('vedicNumerologyPage.calculate')}
                </Button>
              </Grid>
            </Grid>

            {error && (
              <Alert severity={error.includes('local') ? 'info' : 'error'} sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </CardContent>
        </Card>
        )}

        {activeSubTab === 'correction' && (
        <Card sx={{ borderRadius: 3, mb: 4 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Spellcheck color="primary" />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {t('vedicNumerologyPage.correction.title')}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('vedicNumerologyPage.correction.description')}
            </Typography>

            {!authState.isAuthenticated ? (
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: '#f8fff9' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  {t('vedicNumerologyPage.premium.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('vedicNumerologyPage.premium.description')}
                </Typography>
                <Button variant="contained" onClick={() => navigate('/login')}>
                  {t('vedicNumerologyPage.premium.login')}
                </Button>
              </Paper>
            ) : (
              <>
                {!isPremiumUser && (
                  <Alert severity={showPremiumLock ? 'warning' : 'info'} sx={{ mb: 2 }}>
                    {showPremiumLock
                      ? 'Your 3-day trial has ended. Choose a Kundlistar Studio plan to continue.'
                      : 'This studio feature is included during your 3-day trial.'}
                  </Alert>
                )}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label={t('vedicNumerologyPage.dateOfBirth')}
                    value={dob}
                    onChange={(event) => {
                      setDob(formatDateInput(event.target.value));
                      setCorrectionError(null);
                    }}
                    placeholder="DD/MM/YYYY"
                    helperText="Use DD/MM/YYYY format."
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <TextField
                    fullWidth
                    label={t('vedicNumerologyPage.correction.fullName')}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder={t('vedicNumerologyPage.correction.fullNamePlaceholder')}
                  />
                </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={correctionLoading === 'name' ? <CircularProgress size={18} color="inherit" /> : <Spellcheck />}
                    onClick={handleNameCorrection}
                    disabled={correctionLoading !== null || trialLoading}
                    sx={{ minHeight: 56, height: '100%', fontWeight: 600 }}
                  >
                    {t('vedicNumerologyPage.correction.checkName')}
                  </Button>
                </Grid>
                  <Grid size={{ xs: 12, md: 9 }}>
                  <TextField
                    fullWidth
                    label={t('vedicNumerologyPage.correction.mobileNumber')}
                    value={mobile}
                    onChange={(event) => setMobile(event.target.value)}
                    placeholder={t('vedicNumerologyPage.correction.mobilePlaceholder')}
                  />
                </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={correctionLoading === 'mobile' ? <CircularProgress size={18} /> : <PhoneIphone />}
                    onClick={handleMobileCorrection}
                    disabled={correctionLoading !== null || trialLoading}
                    sx={{ minHeight: 56, height: '100%', fontWeight: 600 }}
                  >
                    {t('vedicNumerologyPage.correction.checkMobile')}
                  </Button>
                  </Grid>
                </Grid>
              </>
            )}

            {correctionError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {correctionError}
              </Alert>
            )}

            {(nameCorrection || mobileCorrection) && (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {nameCorrection && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {t('vedicNumerologyPage.correction.nameResult')}
                        </Typography>
	                        <Chip
	                          size="small"
	                          color={getStatusColor(nameCorrection.current.status.status) as any}
	                          label={statusLabel(nameCorrection.current.status.status)}
	                        />
                      </Stack>
                      <Typography variant="body2">{t('vedicNumerologyPage.moolank')}: {nameCorrection.current.moolank}</Typography>
                      <Typography variant="body2">{t('vedicNumerologyPage.bhagyank')}: {nameCorrection.current.bhagyank}</Typography>
                      <Typography variant="body2">
                        {t('vedicNumerologyPage.correction.currentNameNumber')}: {nameCorrection.current.nameTotal} {'->'} {nameCorrection.current.nameNumber}
                      </Typography>
	                      <Typography variant="body2" sx={{ mt: 1 }}>
	                        {t('vedicNumerologyPage.status')}: {statusMessage(nameCorrection.current.status)}
	                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {t('vedicNumerologyPage.correction.luckyNumbers')}: {nameCorrection.current.luckyNumbers.join(', ')}
                      </Typography>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        {t('vedicNumerologyPage.correction.suggestedNames')}
                      </Typography>
                      {nameCorrection.suggestions.length > 0 ? (
                        <Stack spacing={1}>
                          {nameCorrection.suggestions.map((suggestion) => (
                            <Box key={`${suggestion.suggestedName}-${suggestion.nameTotal}`}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {suggestion.suggestedName}
                              </Typography>
	                              <Typography variant="caption" color="text.secondary">
	                                {t('vedicNumerologyPage.correction.total')} {suggestion.nameTotal} {'->'} {suggestion.nameNumber}, {statusLabel(suggestion.status.status)}
	                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('vedicNumerologyPage.correction.noSuggestion')}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                )}

                {mobileCorrection && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {t('vedicNumerologyPage.correction.mobileResult')}
                        </Typography>
	                        <Chip
	                          size="small"
	                          color={getStatusColor(mobileCorrection.status.status) as any}
	                          label={statusLabel(mobileCorrection.status.status)}
	                        />
                      </Stack>
                      <Typography variant="body2">{t('vedicNumerologyPage.moolank')}: {mobileCorrection.moolank}</Typography>
                      <Typography variant="body2">{t('vedicNumerologyPage.bhagyank')}: {mobileCorrection.bhagyank}</Typography>
                      <Typography variant="body2">
                        {t('vedicNumerologyPage.correction.mobileTotal', { total: mobileCorrection.mobileTotal, number: mobileCorrection.mobileNumber })}
                      </Typography>
	                      <Typography variant="body2" sx={{ mt: 1 }}>
	                        {statusMessage(mobileCorrection.status)}
	                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {t('vedicNumerologyPage.correction.bestMobileNumbers', { numbers: mobileCorrection.bestNumbers.join(', ') })}
                      </Typography>
                      {!!mobileCorrection.mobileSignals?.length && (
                        <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                          {mobileCorrection.mobileSignals.map((signal) => {
                            const copy = localizeMobileSignal(signal, mobileCorrection.bestNumbers, isHindi, planetLabel);
                            return (
                              <Paper
                                key={`${signal.type}-${signal.title}`}
                                variant="outlined"
                                sx={{
                                  p: 1,
                                  borderRadius: 1.5,
                                  bgcolor: signal.severity === 'good' ? '#ecfdf5' : signal.severity === 'danger' ? '#fef2f2' : '#fffbeb',
                                  borderColor: signal.severity === 'good' ? '#86efac' : signal.severity === 'danger' ? '#fecaca' : '#fde68a',
                                }}
                              >
                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                                  {copy.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {copy.message}
                                </Typography>
                              </Paper>
                            );
                          })}
                        </Stack>
                      )}
                    </Paper>
                  </Grid>
                )}
              </Grid>
            )}
          </CardContent>
        </Card>
        )}

        {activeSubTab === 'suggestions' && (
        <Card sx={{ borderRadius: 3, mb: 4 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Calculate color="primary" />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Vedic Numerology Suggestions
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Premium suggestions for favourable numbers, starting letters, house, vehicle, mobile, business, baby names and lucky dates.
            </Typography>

            {!authState.isAuthenticated ? (
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: '#f8fff9' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  {t('vedicNumerologyPage.premium.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('vedicNumerologyPage.premium.description')}
                </Typography>
                <Button variant="contained" onClick={() => navigate('/login')}>
                  {t('vedicNumerologyPage.premium.login')}
                </Button>
              </Paper>
            ) : (
              <>
                {!isPremiumUser && (
                  <Alert severity={showPremiumLock ? 'warning' : 'info'} sx={{ mb: 2 }}>
                    {showPremiumLock
                      ? 'Your 3-day trial has ended. Choose a Kundlistar Studio plan to continue.'
                      : 'This studio feature is included during your 3-day trial.'}
                  </Alert>
                )}
                <Grid container spacing={2} alignItems="stretch">
                  <Grid size={{ xs: 12, md: 5 }}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      value={suggestionName}
                      onChange={(event) => {
                        setSuggestionName(event.target.value);
                        setSuggestionsError(null);
                      }}
                      placeholder="Enter full name"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label={t('vedicNumerologyPage.dateOfBirth')}
                      value={suggestionDob}
                      onChange={(event) => {
                        setSuggestionDob(formatDateInput(event.target.value));
                        setSuggestionsError(null);
                      }}
                      placeholder="DD/MM/YYYY"
                      helperText="Use DD/MM/YYYY format."
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={suggestionsLoading ? <CircularProgress size={18} color="inherit" /> : <Calculate />}
                      onClick={handleSuggestions}
                      disabled={suggestionsLoading || trialLoading}
                      sx={{ minHeight: 56, height: '100%', fontWeight: 600 }}
                    >
                      {suggestionsLoading ? 'Analysing...' : 'Get Suggestions'}
                    </Button>
                  </Grid>
                </Grid>

                {suggestionsError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {suggestionsError}
                  </Alert>
                )}

                {suggestionsResult && (
                  <>
                    <NumerologySuggestionsResultView result={suggestionsResult} />
                    {yearlyPlan && (
                      <YearlyNumerologyPlanView
                        plan={yearlyPlan}
                        isPremiumUser={isPremiumUser}
                        isHindi={isHindi}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>
        )}

        {activeSubTab === 'analysis' && result && (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 5 }}>
              <Card sx={{ borderRadius: 3, height: '100%' }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <GridView color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t('vedicNumerologyPage.gridTitle')}
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                      border: '2px solid #92400e',
                      maxWidth: 360,
                      mx: { xs: 'auto', md: 0 },
                      boxShadow: '0 10px 24px rgba(146, 64, 14, 0.12)'
                    }}
                  >
                    {GRID_ORDER.map((num) => renderGridCell(num, [
                      { number: result.ruling, color: BASE_COLOR, label: 'Base' },
                      { number: result.destiny, color: DESTINY_COLOR, label: 'Destiny' },
                    ]))}
                  </Box>

                  {renderGridLegend([
                    { label: 'DOB', color: BASIC_COLOR },
                    { label: 'Base', color: BASE_COLOR, value: result.ruling },
                    { label: 'Destiny', color: DESTINY_COLOR, value: result.destiny },
                  ])}

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {t('vedicNumerologyPage.gridOrder')}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                    Current Dasha Grid
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                      border: '2px solid #92400e',
                      maxWidth: 360,
                      mx: { xs: 'auto', md: 0 },
                      boxShadow: '0 10px 24px rgba(146, 64, 14, 0.12)'
                    }}
                  >
                    {GRID_ORDER.map((num) => renderGridCell(num, [
                      { number: result.ruling, color: BASE_COLOR, label: 'Base' },
                      { number: result.destiny, color: DESTINY_COLOR, label: 'Destiny' },
                      ...(currentMahadashaForTable ? [{ number: currentMahadashaForTable.number, color: MD_COLOR, label: 'MD' }] : []),
                      ...(activeAntardashaForTable ? [{ number: activeAntardashaForTable.number, color: AD_BADGE_COLOR, label: 'AD' }] : []),
                    ]))}
                  </Box>
                  {renderGridLegend([
                    { label: 'DOB', color: BASIC_COLOR },
                    { label: 'Base', color: BASE_COLOR, value: result.ruling },
                    { label: 'Destiny', color: DESTINY_COLOR, value: result.destiny },
                    { label: 'MD', color: MD_COLOR, value: currentMahadashaForTable?.number },
                    { label: 'AD', color: AD_BADGE_COLOR, value: activeAntardashaForTable?.number },
                  ])}
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 7 }}>
              <Card sx={{ borderRadius: 3, mb: 3 }}>
                <CardContent>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Paper variant="outlined" sx={{ p: 2, flex: 1, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('vedicNumerologyPage.moolankRuling')}
                      </Typography>
                      <Typography variant="h3" color="primary.main" sx={{ fontWeight: 600 }}>
                        {result.ruling}
                      </Typography>
                      <Typography variant="body2">{planetLabel(result.ruling)}</Typography>
                    </Paper>
                    <Paper variant="outlined" sx={{ p: 2, flex: 1, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('vedicNumerologyPage.bhagyankDestiny')}
                      </Typography>
                      <Typography variant="h3" color="primary.main" sx={{ fontWeight: 600 }}>
                        {result.destiny}
                      </Typography>
                      <Typography variant="body2">{planetLabel(result.destiny)}</Typography>
                    </Paper>
                    <Paper variant="outlined" sx={{ p: 2, flex: 1, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('vedicNumerologyPage.currentAntardasha')}
                      </Typography>
                      <Typography variant="h3" color="primary.main" sx={{ fontWeight: 600 }}>
                        {result.antardasha}
                      </Typography>
                      <Typography variant="body2">{planetLabel(result.antardasha)}</Typography>
                    </Paper>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
	                    <Chip label={t('vedicNumerologyPage.birthdayChip', { date: formatDisplayDate(result.meta.birthdayThisYear, activeLocale) })} />
                    <Chip label={t('vedicNumerologyPage.yearSumChip', { value: result.meta.yearSum })} />
                    <Chip label={t('vedicNumerologyPage.weekdayChip', { value: result.meta.weekdayNum })} />
                    {source && <Chip color="primary" variant="outlined" label={t('vedicNumerologyPage.sourceChip', { source: source.toUpperCase() })} />}
                  </Stack>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <Timeline color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t('vedicNumerologyPage.mahadashaTimeline')}
                    </Typography>
                  </Stack>

                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('vedicNumerologyPage.table.age')}</TableCell>
                          <TableCell>{t('vedicNumerologyPage.table.number')}</TableCell>
                          <TableCell>{t('vedicNumerologyPage.table.planet')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {result.mahadashas.map((dasha) => (
                          <TableRow key={`${dasha.number}-${dasha.startAge}`}>
                            <TableCell>
                              {dasha.startAge} - {dasha.endAge}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{dasha.number}</TableCell>
                            <TableCell>{planetLabel(dasha.number)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12 }}>
              {isPremiumUser ? (
                <DynamicDashaCharts
                  result={result}
                  activeLocale={activeLocale}
                  planetLabel={planetLabel}
                  formatDisplayDate={formatDisplayDate}
                />
              ) : (
                <PremiumMaskedSection
                  title={premiumMaskCopy.dashaTitle}
                  description={premiumMaskCopy.dashaDescription}
                  premiumLabel={premiumMaskCopy.premiumLabel}
                  ctaLabel={premiumMaskCopy.ctaLabel}
                  teaser="dasha"
                  onUpgrade={() => navigate('/upgrade')}
                />
              )}
            </Grid>

            

            <Grid size={{ xs: 12 }}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    {t('vedicNumerologyPage.antardashaTimeline')}
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('vedicNumerologyPage.table.seq')}</TableCell>
                          <TableCell>{t('vedicNumerologyPage.table.number')}</TableCell>
                          <TableCell>{t('vedicNumerologyPage.table.planet')}</TableCell>
                          <TableCell>{t('vedicNumerologyPage.table.start')}</TableCell>
                          <TableCell>{t('vedicNumerologyPage.table.end')}</TableCell>
                          <TableCell>{t('vedicNumerologyPage.table.days')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {antardashaPeriodsForTable.map((dasha) => (
                          <TableRow key={`${dasha.sequence}-${dasha.number}-${dasha.startDate}`}>
                            <TableCell>{dasha.sequence}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{dasha.number}</TableCell>
                            <TableCell>{planetLabel(dasha.number)}</TableCell>
	                            <TableCell>{formatDisplayDate(dasha.startDate, activeLocale)}</TableCell>
	                            <TableCell>{formatDisplayDate(dasha.endDate, activeLocale)}</TableCell>
                            <TableCell>{dasha.durationDays}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Divider sx={{ mb: 2 }} />

                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    {t('vedicNumerologyPage.pratyantarDasha')}
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('vedicNumerologyPage.table.number')}</TableCell>
                          <TableCell>{t('vedicNumerologyPage.table.planet')}</TableCell>
                          <TableCell>{t('vedicNumerologyPage.table.start')}</TableCell>
                          <TableCell>{t('vedicNumerologyPage.table.end')}</TableCell>
                          <TableCell>{t('vedicNumerologyPage.table.days')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pratyantarPeriodsForTable.map((dasha) => (
                          <TableRow key={`${dasha.number}-${dasha.startDate}`}>
                            <TableCell sx={{ fontWeight: 600 }}>{dasha.number}</TableCell>
                            <TableCell>{planetLabel(dasha.number)}</TableCell>
	                            <TableCell>{formatDisplayDate(dasha.startDate, activeLocale)}</TableCell>
	                            <TableCell>{formatDisplayDate(dasha.endDate, activeLocale)}</TableCell>
                            <TableCell>{dasha.durationDays}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card sx={{ borderRadius: 3, height: '100%', borderLeft: '5px solid #2e7d52' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                        {t('vedicNumerologyPage.strengthsTitle')}
                      </Typography>
                      {result.activePlanes.length > 0 ? (
                        <Stack spacing={1.5}>
                          {result.activePlanes.map((plane) => (
                            <Paper key={plane.name} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {planeName(plane.name)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {planeDesc(plane.name, plane.desc)}
                              </Typography>
                            </Paper>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('vedicNumerologyPage.noActivePlanes')}
                        </Typography>
                      )}
	                    </CardContent>
	                  </Card>
	                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  {isPremiumUser ? (
                    <Card sx={{ borderRadius: 3, height: '100%', borderLeft: '5px solid #7c3aed' }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#6d28d9' }}>
                          Vedic Grid Yogas
                        </Typography>
                        {!!result.yogas?.length ? (
                          <Stack spacing={1.5}>
                            {result.yogas.map((yoga: any) => (
                              <Paper key={yoga.id || yoga.name} variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#faf5ff' }}>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                                  <Chip size="small" color="secondary" label={yoga.type} />
                                  <Chip size="small" variant="outlined" label={(yoga.numbers || []).join('-')} />
                                </Stack>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {yoga.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {yoga.effect}
                                </Typography>
                              </Paper>
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No major Vedic grid yoga found from the current DOB grid.
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  ) : (
                    <PremiumMaskedSection
                      title={premiumMaskCopy.yogasTitle}
                      description={premiumMaskCopy.yogasDescription}
                      premiumLabel={premiumMaskCopy.premiumLabel}
                      ctaLabel={premiumMaskCopy.ctaLabel}
                      teaser="yogas"
                      onUpgrade={() => navigate('/upgrade')}
                    />
                  )}
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Card sx={{ borderRadius: 3, height: '100%', borderLeft: '5px solid #c2410c' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#9a3412' }}>
                        {t('vedicNumerologyPage.remediesTitle')}
                      </Typography>
                      {result.missing.length > 0 ? (
                        <Stack spacing={1.5}>
                          {result.missing.map((item) => (
                            <Paper key={item.num} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {t('vedicNumerologyPage.missingNumber', { num: item.num, name: planetLabel(item.num) })}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                {planetQuality(item.num, item.quality)}
                              </Typography>
                              <Typography variant="body2">
                                {planetRemedy(item.num, item.remedy)}
                              </Typography>
                            </Paper>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('vedicNumerologyPage.allNumbersPresent')}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Container>
      <PremiumTrialPopup
        open={popup.open}
        featureName={popup.featureName}
        used={popup.used}
        total={popup.total}
        remaining={popup.remaining}
        loading={trialLoading || correctionLoading !== null || suggestionsLoading}
        onCancel={closePopup}
        onContinue={popup.nextAction || (() => Promise.resolve())}
      />
    </>
  );
};

const NumberCard = ({ item }: { item: any }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
    <Typography variant="h3" color="primary.main" sx={{ fontWeight: 600 }}>
      {item.number}
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{item.planet}</Typography>
    <Typography variant="body2">{item.status} · {item.score}/100</Typography>
  </Paper>
);

const SuggestionList = ({ title, items }: { title: string; items: any[] }) => {
  if (!items?.length) return null;
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>{title}</Typography>
      <Grid container spacing={2}>
        {items.map((item) => (
          <Grid key={`${title}-${item.rootNumber}`} size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Number vibration {item.rootNumber} - {item.planet} · {item.score}/100
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>{item.suggestion}</Typography>
              {!!item.startingLetters?.length && <Typography variant="body2" sx={{ mt: 1 }}><strong>Starting Letters:</strong> {item.startingLetters.join(', ')}</Typography>}
              {!!item.sampleTotals?.length && <Typography variant="body2" sx={{ mt: 1 }}><strong>Good Totals:</strong> {item.sampleTotals.join(', ')}</Typography>}
              {!!item.fullNumberTotals?.length && <Typography variant="body2" sx={{ mt: 1 }}><strong>Full Mobile Totals:</strong> {item.fullNumberTotals.join(', ')}</Typography>}
              {!!item.lastFourDigitTotals?.length && <Typography variant="body2" sx={{ mt: 1 }}><strong>Last 4 Digit Totals:</strong> {item.lastFourDigitTotals.join(', ')}</Typography>}
              {!!item.suggestedNameTotals?.length && <Typography variant="body2" sx={{ mt: 1 }}><strong>Name Totals:</strong> {item.suggestedNameTotals.join(', ')}</Typography>}
              {!!item.examples?.length && <Typography variant="body2" sx={{ mt: 1 }}><strong>Examples:</strong> {item.examples.join(', ')}</Typography>}
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const NumerologySuggestionsResultView = ({ result }: { result: NumerologySuggestionsResult }) => (
  <Box sx={{ mt: 3 }}>
    <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>Your Core Numbers</Typography>
    <Grid container spacing={2}>
      {Object.entries(result.userNumbers).map(([key, item]: [string, any]) => (
        <Grid key={key} size={{ xs: 12, md: 4 }}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Typography variant="h3" color="primary.main" sx={{ fontWeight: 600 }}>{item.number}</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{key.replace(/Number$/, ' Number')}</Typography>
            <Typography variant="caption" color="text.secondary">{item.planet}{item.total ? ` · Total ${item.total}` : ''}</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>{item.meaning}</Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>

    <Typography variant="h6" sx={{ mt: 3, mb: 1.5, fontWeight: 600 }}>Favourable Numbers</Typography>
    <Grid container spacing={2}>
      {result.preferredNumbers.map((item) => (
        <Grid key={item.number} size={{ xs: 6, md: 3 }}>
          <NumberCard item={item} />
        </Grid>
      ))}
    </Grid>

    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>Name Starting Letters</Typography>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
        {Object.entries(result.suggestions.nameStartingLetters || {}).map(([number, letters]: [string, any]) => (
          <Chip key={number} label={`Number ${number}: ${(letters || []).join(', ')}`} />
        ))}
      </Stack>
    </Box>

    <SuggestionList title="House / Flat / Office Number Suggestions" items={result.suggestions.houseNumbers} />
    <SuggestionList title="Vehicle Number Suggestions" items={result.suggestions.vehicleNumbers} />
    <SuggestionList title="Mobile Number Suggestions" items={result.suggestions.mobileNumbers} />
    <SuggestionList title="Business Name Suggestions" items={result.suggestions.businessNames} />
    <SuggestionList title="Baby Name Suggestions" items={result.suggestions.babyNames} />

    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>Lucky Dates</Typography>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
        {(result.suggestions.luckyDates || []).map((item: any) => (
          <Chip key={item.day} label={`${item.day} (${item.planet})`} />
        ))}
      </Stack>
    </Box>

    {!!result.avoidNumbers?.length && (
      <>
        <Typography variant="h6" sx={{ mt: 3, mb: 1.5, fontWeight: 600 }}>Numbers to Avoid</Typography>
        <Grid container spacing={2}>
          {result.avoidNumbers.map((item) => (
            <Grid key={item.number} size={{ xs: 6, md: 3 }}>
              <NumberCard item={item} />
            </Grid>
          ))}
        </Grid>
      </>
    )}
  </Box>
);

const YearlyNumerologyPlanView = ({
  plan,
  isPremiumUser,
  isHindi,
}: {
  plan: YearlyNumerologyPlanResult;
  isPremiumUser: boolean;
  isHindi: boolean;
}) => (
  <Box sx={{ mt: 4 }}>
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
      <CalendarMonth color="primary" />
      <Typography variant="h5" sx={{ fontWeight: 600 }}>
        {isHindi ? 'वार्षिक Numerology Plan Preview' : 'Yearly Numerology Plan Preview'}
      </Typography>
    </Stack>

    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2.5, bgcolor: '#f8fff9', mb: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
        <Box
          sx={{
            width: 104,
            minWidth: 104,
            height: 104,
            borderRadius: 2,
            bgcolor: 'primary.main',
            color: 'common.white',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            px: 1,
          }}
        >
          <Typography component="div" sx={{ fontWeight: 600, fontSize: 44, lineHeight: 1, color: 'inherit' }}>
            {plan.personalYear.number}
          </Typography>
          <Typography component="div" sx={{ mt: 0.75, fontWeight: 600, fontSize: 15, lineHeight: 1.1, color: 'inherit' }}>
            {plan.personalYear.planet}
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{plan.personalYear.title}</Typography>
          <Typography variant="body2" color="text.secondary">{plan.personalYear.publicSummary}</Typography>
          {!isPremiumUser && (
            <Chip size="small" icon={<Lock />} label={isHindi ? 'Full yearly reading locked है' : 'Full yearly reading locked'} sx={{ mt: 1 }} />
          )}
        </Box>
      </Stack>
    </Paper>

    <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>{isHindi ? '12 महीने की energy cards' : '12-Month Energy Cards'}</Typography>
    <Grid container spacing={1.5}>
      {plan.months.map((month) => (
        <Grid key={month.month} size={{ xs: 12, sm: 6, md: 4 }}>
          <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2, height: '100%' }}>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{month.monthName}</Typography>
                <Typography variant="caption" color="text.secondary">{month.title}</Typography>
              </Box>
              <Chip size="small" label={`${month.personalMonth} ${month.planet}`} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{month.publicSummary}</Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              {month.goodDates.slice(0, 3).map((date) => (
                <Chip key={date.date} size="small" variant="outlined" label={date.date} />
              ))}
              {month.lockedDatesCount > 0 && <Chip size="small" label={isHindi ? `+${month.lockedDatesCount} locked` : `+${month.lockedDatesCount} locked`} />}
            </Stack>
          </Paper>
        </Grid>
      ))}
    </Grid>

    <Typography variant="h6" sx={{ mt: 3, mb: 1.5, fontWeight: 600 }}>{isHindi ? 'Premium support scores' : 'Premium Support Scores'}</Typography>
    <Grid container spacing={1.5}>
      {plan.eventForecast.map((event) => (
        <Grid key={event.key} size={{ xs: 12, sm: 6, md: 4 }}>
          <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2, height: '100%', borderStyle: event.locked ? 'dashed' : 'solid' }}>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{event.title}</Typography>
              <Chip size="small" color={event.score === null ? 'default' : 'primary'} label={event.score === null ? 'Report' : `${event.score}/100`} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{event.publicSummary}</Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 1, fontWeight: 600 }}>{event.rating}</Typography>
            {event.locked && <Typography variant="caption" color="text.secondary">{event.lockMessage}</Typography>}
          </Paper>
        </Grid>
      ))}
    </Grid>

    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>{plan.disclaimer}</Typography>
  </Box>
);

export default VedicNumerology;
